'use client';

import { GripVertical } from 'lucide-react';
import React, {
  DragEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import { cn } from '~/lib/utils';

export interface DragHandleProps {
  className?: string;
  size?: number;
  disabled?: boolean;
}

export const DragHandle: React.FC<DragHandleProps> = ({
  className,
  size = 12,
  disabled = false,
}) => {
  if (disabled) return null;

  return (
    <div
      className={cn(
        'text-content-secondary cursor-grab active:cursor-grabbing',
        'hover:text-content-primary transition-colors duration-200',
        className,
      )}
      aria-label="Drag handle"
    >
      <GripVertical size={size} />
    </div>
  );
};

// Types
export interface DraggableItem {
  id: string;
  content: ReactNode;
  data?: unknown;
}

export interface DraggableListProps {
  items: DraggableItem[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  className?: string;
  itemClassName?: string;
  dragHandleClassName?: string;
  disabled?: boolean;
  showDragHandle?: boolean;
  renderItem?: (item: DraggableItem, index: number) => ReactNode;
  customDragHandle?: boolean; // When true, expects drag handle to be inside the content
  'aria-label'?: string;
}

export interface DraggableItemComponentProps {
  item: DraggableItem;
  index: number;
  isDragging: boolean;
  isDragOver: boolean;
  disabled: boolean;
  showDragHandle: boolean;
  customDragHandle: boolean;
  dragHandleClassName?: string;
  itemClassName?: string;
  onDragStart: (event: DragEvent<HTMLDivElement>, index: number) => void;
  onDragOver: (event: DragEvent<HTMLDivElement>, index: number) => void;
  onDragLeave: () => void;
  onDrop: (event: DragEvent<HTMLDivElement>, index: number) => void;
  onDragEnd: (event: DragEvent<HTMLDivElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>, index: number) => void;
  renderItem?: (item: DraggableItem, index: number) => ReactNode;
  children?: ReactNode;
}

// Custom hook for drag and drop logic
const useDragAndDrop = (
  onReorder: (fromIndex: number, toIndex: number) => void,
) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((event: DragEvent, index: number) => {
    setDraggedIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', index.toString());

    // Add visual feedback
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.style.opacity = '0.5';
    }
  }, []);

  const handleDragOver = useCallback((event: DragEvent, index: number) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent, dropIndex: number) => {
      event.preventDefault();
      const dragIndex = parseInt(event.dataTransfer.getData('text/plain'), 10);

      if (!isNaN(dragIndex) && dragIndex !== dropIndex) {
        onReorder(dragIndex, dropIndex);
      }

      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [onReorder],
  );

  const handleDragEnd = useCallback((event: DragEvent) => {
    // Reset visual feedback
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.style.opacity = '';
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  return {
    draggedIndex,
    dragOverIndex,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  };
};

// Individual draggable item component
const DraggableItemComponent: React.FC<DraggableItemComponentProps> =
  React.memo(
    ({
      item,
      index,
      isDragging,
      isDragOver,
      disabled,
      showDragHandle,
      customDragHandle,
      dragHandleClassName,
      itemClassName,
      onDragStart,
      onDragOver,
      onDragLeave,
      onDrop,
      onDragEnd,
      onKeyDown,
      renderItem,
      children,
    }) => {
      const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
          onKeyDown(event, index);
        },
        [onKeyDown, index],
      );

      return (
        <div
          draggable={!disabled}
          onDragStart={(e) => onDragStart(e, index)}
          onDragOver={(e) => onDragOver(e, index)}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(e, index)}
          onDragEnd={onDragEnd}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-label={`Draggable item ${index + 1}${isDragging ? ', currently being dragged' : ''}`}
          className={cn(
            'transition-all duration-200',
            !disabled && 'cursor-move focus:outline-none',
            isDragging && 'opacity-50 transform scale-[0.98]',
            isDragOver &&
              'border-content-secondary bg-content-secondary/10 transform scale-[1.02]',
            itemClassName,
          )}
        >
          {customDragHandle ? (
            // When using custom drag handle, render content as-is (drag handle should be inside)
            <div className="w-full">
              {renderItem ? renderItem(item, index) : children || item.content}
            </div>
          ) : (
            // Default layout with external drag handle
            <div className="flex items-center">
              {showDragHandle && !disabled && (
                <DragHandle className={cn('p-1 mr-2', dragHandleClassName)} />
              )}
              <div className="flex-1">
                {renderItem
                  ? renderItem(item, index)
                  : children || item.content}
              </div>
            </div>
          )}
        </div>
      );
    },
  );

DraggableItemComponent.displayName = 'DraggableItemComponent';

// Main DraggableList component
const DraggableList: React.FC<DraggableListProps> = ({
  items,
  onReorder,
  className,
  itemClassName,
  dragHandleClassName,
  disabled = false,
  showDragHandle = true,
  customDragHandle = false,
  renderItem,
  'aria-label': ariaLabel = 'Draggable list',
}) => {
  const {
    draggedIndex,
    dragOverIndex,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop(onReorder);

  // Keyboard navigation support
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, index: number) => {
      if (disabled) return;

      const { key, ctrlKey, metaKey } = event;
      const isModifierPressed = ctrlKey || metaKey;

      switch (key) {
        case 'ArrowUp':
          if (isModifierPressed && index > 0) {
            event.preventDefault();
            onReorder(index, index - 1);
          }
          break;
        case 'ArrowDown':
          if (isModifierPressed && index < items.length - 1) {
            event.preventDefault();
            onReorder(index, index + 1);
          }
          break;
        case 'Home':
          if (isModifierPressed && index > 0) {
            event.preventDefault();
            onReorder(index, 0);
          }
          break;
        case 'End':
          if (isModifierPressed && index < items.length - 1) {
            event.preventDefault();
            onReorder(index, items.length - 1);
          }
          break;
        default:
          break;
      }
    },
    [disabled, items.length, onReorder],
  );

  if (items.length === 0) {
    return (
      <div
        className={cn('text-content-tertiary text-center py-4', className)}
        aria-label="Empty list"
      >
        No items to display
      </div>
    );
  }

  return (
    <div
      className={cn('space-y-2', className)}
      role="list"
      aria-label={ariaLabel}
      aria-describedby={disabled ? undefined : 'draggable-list-instructions'}
    >
      {!disabled && (
        <div
          id="draggable-list-instructions"
          className="sr-only"
          aria-live="polite"
        >
          Use Ctrl+Arrow keys to reorder items, or drag and drop with mouse
        </div>
      )}

      {items.map((item, index) => (
        <DraggableItemComponent
          key={item.id}
          item={item}
          index={index}
          isDragging={draggedIndex === index}
          isDragOver={dragOverIndex === index}
          disabled={disabled}
          showDragHandle={showDragHandle}
          customDragHandle={customDragHandle}
          dragHandleClassName={dragHandleClassName}
          itemClassName={itemClassName}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onDragEnd={handleDragEnd}
          onKeyDown={handleKeyDown}
          renderItem={renderItem}
        />
      ))}
    </div>
  );
};

export default DraggableList;
